import { CSSProperties, FC, useMemo } from "react";
import { FootballProps } from "..";
import { CardsAndCorners } from "./components/CardsAndCorners";
import { ActionAreas } from "./components/ActionAreas";
import { getTeamColors } from "components/colors";
import { Chart } from "components/charts/Chart";
import { LineChart } from "components/charts/LineChart";
import { Text } from "components/common/Text";

const extract = (splitStatus: any, keyOrKeys: string | string[]) => {
  if (!splitStatus || !Array.isArray(splitStatus)) {
    return [0, 0] as [number, number];
  }

  const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];

  const item = splitStatus.find((status: string) => {
    if (!status || typeof status !== "string") return false;
    return keys.some((k) => status.startsWith(k));
  });

  if (!item) {
    return [0, 0] as [number, number];
  }

  const dataString = item.split("=")[1];
  if (!dataString) return [0, 0] as [number, number];

  const toNum = (v: string) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const data = dataString.split(":").map(toNum);
  if (!(data && data.length === 2)) return [0, 0] as [number, number];

  return [data[0], data[1]] as [number, number];
};

export const Stats: FC<FootballProps> = ({ language, status }) => {
  const { home, away } = getTeamColors(status as any);
  const data = useMemo(() => {
    if (!status || typeof status !== "object") {
      return {};
    }
    const splitStatus = status.sts?.split("|");
    if (!splitStatus) {
    }

    const attacks = extract(splitStatus, "Attacks");
    const dangerousAttacks = extract(splitStatus, "Dangerous Attacks");
    const possession = extract(splitStatus, "Possession %");
    const expectedGoals = extract(splitStatus, ["XG", "xG", "xg"]);

    const shotsOnTarget = extract(splitStatus, ["On Target", "SHOT_ON", "SOT"]);
    const shotsOffTarget = extract(splitStatus, [
      "Off Target",
      "SHOT_OFF",
      "SOFF",
    ]);
    const corners = extract(splitStatus, ["CORNER", "Corners"]);
    const yellowCards = extract(splitStatus, ["YELLOW_CARD", "Yellow Cards"]);
    const redCards = extract(splitStatus, ["RED_CARD", "Red Cards"]);

    // AA: Action Areas (home% : away%), middle = 100 - (home + away)
    const aaRaw = extract(splitStatus, "AA");
    const fieldShare = {
      home: Number.isFinite(aaRaw[0]) ? aaRaw[0] : 0,
      away: Number.isFinite(aaRaw[1]) ? aaRaw[1] : 0,
      middle: Math.max(
        0,
        100 -
          ((Number.isFinite(aaRaw[0]) ? aaRaw[0] : 0) +
            (Number.isFinite(aaRaw[1]) ? aaRaw[1] : 0))
      ),
    };

    const crosses = extract(splitStatus, ["CR", "Crosses"]);
    const passAccuracy = extract(splitStatus, ["PA", "Pass Accuracy"]);
    const keyPasses = extract(splitStatus, ["KP", "Key Passes"]);
    const goalkeeperSaves = extract(splitStatus, [
      "SV",
      "Saves",
      "Goalkeeper Saves",
    ]);

    const shotsTotal: [number, number] = [
      (shotsOnTarget?.[0] || 0) + (shotsOffTarget?.[0] || 0),
      (shotsOnTarget?.[1] || 0) + (shotsOffTarget?.[1] || 0),
    ];

    return {
      attacks,
      dangerousAttacks,
      possession,
      expectedGoals,
      shotsOnTarget,
      shotsOffTarget,
      shotsTotal,
      corners,
      yellowCards,
      redCards,
      fieldShare,
      crosses,
      passAccuracy,
      keyPasses,
      goalkeeperSaves,
    };
  }, [status]);

  const colorVars = {
    "--home-color": home,
    "--away-color": away,
  } as CSSProperties;

  return (
    <div
      className="flex flex-col items-center justify-start gap-[2vh]"
      style={colorVars}
    >
      {data.expectedGoals &&
        (data.expectedGoals[0] !== 0 || data.expectedGoals[1] !== 0) && (
          <div className="flex w-full max-w-[30vw] items-center justify-between font-bold text-white">
            <Text variant="chartTitle">
              {Number(data.expectedGoals[0]).toFixed(2)}
            </Text>
            <span className="font-bold uppercase text-statisticTitle">
              <Text variant="chartValue">xG</Text>
            </span>
            <Text variant="chartTitle">
              {Number(data.expectedGoals[1]).toFixed(2)}
            </Text>
          </div>
        )}

      <div className="flex w-full gap-[2vw] justify-between">
        <Chart
          homeValue={data.attacks ? data.attacks[0] : 0}
          awayValue={data.attacks ? data.attacks[1] : 0}
          title="Attacks"
        />
        <Chart
          homeValue={data.dangerousAttacks ? data.dangerousAttacks[0] : 0}
          awayValue={data.dangerousAttacks ? data.dangerousAttacks[1] : 0}
          title="Dangerous Attacks"
        />
        <Chart
          homeValue={data.possession ? data.possession[0] : 0}
          awayValue={data.possession ? data.possession[1] : 0}
          title="Possession %"
        />
      </div>

      <CardsAndCorners
        corners={data.corners || [0, 0]}
        yellowCards={data.yellowCards || [0, 0]}
        redCards={data.redCards || [0, 0]}
        onTarget={data.shotsOnTarget || [0, 0]}
        offTarget={data.shotsOffTarget || [0, 0]}
      />

      {data.fieldShare &&
        (data.fieldShare.home !== 0 ||
          data.fieldShare.middle !== 0 ||
          data.fieldShare.away !== 0) && (
          <ActionAreas
            home={data.fieldShare.home}
            middle={data.fieldShare.middle}
            away={data.fieldShare.away}
          />
        )}

      <div className="flex w-full flex-col gap-[2vw] pt-[4vw]">
        {data.keyPasses &&
          (data.keyPasses[0] !== 0 || data.keyPasses[1] !== 0) && (
            <LineChart
              homeValue={data.keyPasses[0]}
              awayValue={data.keyPasses[1]}
              title="Key Passes"
            />
          )}

        {data.goalkeeperSaves &&
          (data.goalkeeperSaves[0] !== 0 || data.goalkeeperSaves[1] !== 0) && (
            <LineChart
              homeValue={data.goalkeeperSaves[0]}
              awayValue={data.goalkeeperSaves[1]}
              title="Goalkeeper Saves"
            />
          )}

        {data.crosses && (data.crosses[0] !== 0 || data.crosses[1] !== 0) && (
          <LineChart
            homeValue={data.crosses[0]}
            awayValue={data.crosses[1]}
            title="Crosses"
          />
        )}

        {data.passAccuracy &&
          (data.passAccuracy[0] !== 0 || data.passAccuracy[1] !== 0) && (
            <LineChart
              homeValue={data.passAccuracy[0]}
              awayValue={data.passAccuracy[1]}
              title="Pass Accuracy"
              valueFormatter={(v) =>
                `%${Math.max(
                  0,
                  Math.min(100, Math.round(v <= 1 ? v * 100 : v))
                )}`
              }
            />
          )}
      </div>
    </div>
  );
};
